import { useMemo } from 'react';
import * as Yup from 'yup';

interface UseCustomFieldsOptions {
  space?: SpaceType;
  isFree?: boolean;
  isSpanish?: boolean;
}

export const useCustomFields = ({ space, isFree = false, isSpanish = true }: UseCustomFieldsOptions) => {
  const filteredFields = useMemo(() => {
    if (!space?.custom_form_fields) {
      return [];
    }
    
    const filtered = space.custom_form_fields
      .filter(field => {
        if (isFree && field.show_for_free_events === false) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.order - b.order);
    
    return filtered;
  }, [space?.custom_form_fields, isFree]);

  const getInitialValues = useMemo(() => {
    const initialValues: Record<string, any> = {};
    
    filteredFields.forEach(field => {
      switch (field.field_type) {
        case 'checkbox':
          initialValues[field.name] = false;
          break;
        case 'number':
          initialValues[field.name] = '';
          break;
        default:
          initialValues[field.name] = '';
      }
    });

    return initialValues;
  }, [filteredFields]);

  const getValidationSchema = useMemo(() => {
    if (!filteredFields.length) return Yup.object({});

    const schemaFields: Record<string, any> = {};

    filteredFields.forEach(field => {
      let fieldSchema: any;

      switch (field.field_type) {
        case 'email':
          fieldSchema = Yup.string().email(
            isSpanish ? 'Email inválido' : 'Invalid email'
          );
          break;

        case 'number':
          fieldSchema = Yup.number().typeError(
            isSpanish ? 'Debe ser un número' : 'Must be a number'
          );
          
          if (field.validation_rules?.min_value !== undefined) {
            fieldSchema = fieldSchema.min(
              field.validation_rules.min_value,
              isSpanish ? 
                `El valor mínimo es ${field.validation_rules.min_value}` :
                `Minimum value is ${field.validation_rules.min_value}`
            );
          }
          
          if (field.validation_rules?.max_value !== undefined) {
            fieldSchema = fieldSchema.max(
              field.validation_rules.max_value,
              isSpanish ?
                `El valor máximo es ${field.validation_rules.max_value}` :
                `Maximum value is ${field.validation_rules.max_value}`
            );
          }
          break;

        case 'phone':
          fieldSchema = Yup.string().matches(
            /^[+]?[0-9\s\-()]{10,}$/,
            isSpanish ? 'Formato de teléfono inválido' : 'Invalid phone format'
          );
          break;

        case 'date':
          fieldSchema = Yup.date().typeError(
            isSpanish ? 'Fecha inválida' : 'Invalid date'
          );
          break;

        case 'select':
          if (field.options && field.options.length > 0) {
            fieldSchema = Yup.string().oneOf(
              field.options,
              isSpanish ? 'Opción no válida' : 'Invalid option'
            );
          } else {
            fieldSchema = Yup.string();
          }
          break;

        case 'checkbox':
          fieldSchema = Yup.boolean();
          break;

        default:
          fieldSchema = Yup.string();
      }

      if (['text', 'textarea'].includes(field.field_type)) {
        if (field.validation_rules?.min_length) {
          fieldSchema = fieldSchema.min(
            field.validation_rules.min_length,
            isSpanish ?
              `Mínimo ${field.validation_rules.min_length} caracteres` :
              `Minimum ${field.validation_rules.min_length} characters`
          );
        }
        
        if (field.validation_rules?.max_length) {
          fieldSchema = fieldSchema.max(
            field.validation_rules.max_length,
            isSpanish ?
              `Máximo ${field.validation_rules.max_length} caracteres` :
              `Maximum ${field.validation_rules.max_length} characters`
          );
        }

        if (field.validation_rules?.pattern) {
          fieldSchema = fieldSchema.matches(
            new RegExp(field.validation_rules.pattern),
            field.validation_rules.message || 
            (isSpanish ? 'Formato inválido' : 'Invalid format')
          );
        }
      }

      if (field.required) {
        if (field.field_type === 'checkbox') {
          fieldSchema = fieldSchema.oneOf(
            [true],
            isSpanish ? 'Este campo es obligatorio' : 'This field is required'
          );
        } else {
          fieldSchema = fieldSchema.required(
            isSpanish ? 'Este campo es obligatorio' : 'This field is required'
          );
        }
      }

      schemaFields[field.name] = fieldSchema;
    });

    return Yup.object(schemaFields);
  }, [filteredFields, isSpanish]);

  const extractCustomFieldsData = (formValues: any): Record<string, any> => {
    const customFieldsData: Record<string, any> = {};
    
    filteredFields.forEach(field => {
      const value = formValues[field.name];
      if (value !== undefined && value !== '' && value !== false) {
        if (field.field_type === 'phone' && typeof value === 'object' && value.number) {
          let phoneNumber = value.number;
          
          if (typeof phoneNumber === 'string' && phoneNumber.startsWith('"') && phoneNumber.endsWith('"')) {
            phoneNumber = phoneNumber.slice(1, -1);
          }
          
          customFieldsData[field.name] = phoneNumber;
        } else {
          let fieldValue = value;
          if (typeof fieldValue === 'string' && fieldValue.startsWith('"') && fieldValue.endsWith('"')) {
            fieldValue = fieldValue.slice(1, -1);
          }
          customFieldsData[field.name] = fieldValue;
        }
      }
    });

    return customFieldsData;
  };

  return {
    customFields: filteredFields,
    loading: false,
    error: null,
    hasCustomFields: filteredFields.length > 0,
    getInitialValues,
    getValidationSchema,
    extractCustomFieldsData,
  };
};